




































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import { formatDigitsGrouping } from "../../../common/utils/formattingUtils"
import i18nService from "@/services/i18n";

type Column = 'action' | 'kat' | 'cls' | 'pcl' | 'gr' | 'pgr' | 'abp' | 'prg' | 'ppr' | 'spf' | 'gu' | 'expense_code' | 'total_amount' | 'month1' | 'month2' | 'month3' | 'month4' | 'month5' | 'month6' | 'month7' | 'month8' | 'month9' | 'month10' | 'month11' | 'month12'

export interface TableField {
    key: Column,
    label: string,
    active: boolean,
    monthIndex?: number  // 0-based index for month1-12 columns; undefined for non-month
}

// Number of rows kept in the DOM at any time
const WINDOW_SIZE = 200
// Rows rendered above/below the visible viewport as a scroll buffer
const SCROLL_BUFFER = 60

@Component({})
export default class ReportSvodPlan extends Vue {

    @Prop({
        type: Object,
        required: true,
    })
    public tableData!: any

    @Prop({
        type: String,
        required: true,
    })
    public planType!: any

    @Prop({
        type: Boolean,
        required: true,
    })
    public calcOnCurPeriod!: boolean

    @Prop({
        type: Boolean,
        default: false,
    })
    public showTransferts!: boolean

    @Prop({
        type: String,
        required: true,
    })
    public roundUp!: string

    private created() {
        if (this.planType === 'PF') {
            this.getPFRows();
        } else if (this.planType === 'PP') {
            this.getPPRows();
        }
        this.calcItems();
        this.columnsNameMap = { 'action': '' }

        for (const column of this.tableData.selected_levels) {
            if (this.allColumnsNameMap[column]) {
                this.columnsNameMap[column] = this.allColumnsNameMap[column]
            }
        }

        this.columnsNameMap['total_amount'] = this.allColumnsNameMap['total_amount']

        for (let i = 1; i <= 12; i++) {
            const monthKey = `month${i}`
            this.columnsNameMap[monthKey] = this.allColumnsNameMap[monthKey]
        }

        this.activeTableFields = this.tableFields.filter(f => f.active)
    }

    private mounted() {
        this.$nextTick(() => {
            const container = this.$refs.tableContainer as HTMLElement
            if (!container) return
            const firstRow = container.querySelector('tbody tr') as HTMLElement | null
            if (firstRow && firstRow.offsetHeight > 0) {
                this.rowHeight = firstRow.offsetHeight
            }
            this.initRowHeights(this.filteredRows.length)
            this.virtualEnd = Math.min(WINDOW_SIZE, this.filteredRows.length)
            // Capture-phase listener catches scroll on ANY element in the document tree —
            // works regardless of whether main-container, a parent div, or window scrolls.
            window.addEventListener('scroll', this.onScroll, { passive: true, capture: true })
        })
    }

    // Build/rebuild _rowHeights (all set to current rowHeight) and _prefixSum.
    // Called when filteredRows length changes (data load, tree expand/collapse).
    private initRowHeights(count: number) {
        const rh = this.rowHeight
        this._rowHeights = new Array(count).fill(rh)
        this.rebuildPrefixSum()
    }

    // O(n) full rebuild of prefix sum. Non-reactive — does not trigger Vue re-render.
    private rebuildPrefixSum() {
        const n = this._rowHeights.length
        const ps = new Array(n + 1)
        ps[0] = 0
        for (let i = 0; i < n; i++) {
            ps[i + 1] = ps[i] + this._rowHeights[i]
        }
        this._prefixSum = ps
    }

    // Binary search: largest i where _prefixSum[i] <= scrollPos.
    private findRowAtScrollPos(scrollPos: number): number {
        if (this._prefixSum.length < 2) return Math.floor(scrollPos / this.rowHeight)
        let lo = 0
        let hi = this._prefixSum.length - 2
        while (lo < hi) {
            const mid = (lo + hi + 1) >> 1
            if (this._prefixSum[mid] <= scrollPos) lo = mid
            else hi = mid - 1
        }
        return lo
    }

    // Measure rendered row heights and update _prefixSum if anything changed.
    // Called via $nextTick after each virtual window update — does not cause re-render.
    private measureRenderedRows() {
        const container = this.$refs.tableContainer as HTMLElement
        if (!container || !this._rowHeights.length) return
        const rows = container.querySelectorAll<HTMLElement>('tbody tr[data-row-index]')
        let changed = false
        rows.forEach(row => {
            const idx = parseInt(row.dataset.rowIndex ?? '')
            const h = row.offsetHeight
            if (!isNaN(idx) && h > 0 && idx < this._rowHeights.length && this._rowHeights[idx] !== h) {
                this._rowHeights[idx] = h
                changed = true
            }
        })
        if (changed) this.rebuildPrefixSum()
    }

    private beforeDestroy() {
        window.removeEventListener('scroll', this.onScroll, true)
        if (this._scrollRaf !== null) cancelAnimationFrame(this._scrollRaf)
        if (this._scrollPointerTimer !== null) clearTimeout(this._scrollPointerTimer)
    }

    public tr(key: any): string {
        if (typeof key !== 'string') {
            return ''
        }

        const translationKey = 'modules.budget_execution_monitoring.reports_constructor'
        const text: string = String(this.$t(`${translationKey}.${key}`))

        return text
    }

    private get nameSuffix(): string {
        return `${i18nService.locale}`
    }

    private rowName(row: any, key: string): string {
        return row[`${key}_name_${this.nameSuffix}`] ?? ''
    }

    private getCellText(row: any, key: string): string {
        if (key === 'transfert') {
            const code = row.expense_code ?? ''
            const name = row[`project_name_${this.nameSuffix}`] ?? ''
            return `${code} ${name}`.trim()
        }
        const val = row[key] ?? ''
        const name = row[`${key}_name_${this.nameSuffix}`] ?? ''
        return `${val} ${name}`.trim()
    }

    private appText(app: any) {
        return `${app[`name_${this.nameSuffix}`]} ${app[`full_name_${this.nameSuffix}`]}`
    }

    private get totalLength() {
        let total = 8;
        for (const item of this.topLevelDatas) {
            if (String(item).length > total) {
                total = String(item).length
            }
        }
        return total
    }

    private topLevelDatas: number[] = []

    private flattenData(data: any[], parentData: any = null, isFirstLevel: boolean = true, multiplier: number = Math.pow(10, Number(this.roundUp))): any[] {
        const result: any[] = [];

        const childLists = [
            'kat_list',
            'cls_list',
            'pcl_list',
            'pgr_list',
            'abp_list',
            'prg_list',
            'ppr_list',
            'spf_list',
            'gr_list',
            'gu_list',
            'region_list'
        ];

        for (const item of data) {
            const hasRegularChildren = childLists.some(listKey => Array.isArray(item[listKey]) && item[listKey].length > 0);
            const hasProjectChildren = Array.isArray(item['project_list']) && item['project_list'].length > 0;
            const isLeaf = !hasRegularChildren && !hasProjectChildren;

            const flattenedItem: any = {
                ...item,
                months_sum: item.months_sum ?? null,
                visible: isFirstLevel,
                open: false,
                isLeaf: isLeaf
            };
            // Strip nested child arrays — they're never read from flattened rows and
            // their presence makes Vue's reactivity observer O(n²).
            for (const key of childLists) delete flattenedItem[key];
            delete flattenedItem.project_list;

            // Pre-compute formatted numeric cells here (before Vue observes the object)
            // so the template never calls Math.pow/Math.round/formatDigitsGrouping per render.
            if (flattenedItem.months_sum) {
                flattenedItem._totalFormatted = formatDigitsGrouping(
                    Math.round(flattenedItem.months_sum.total * multiplier) / multiplier
                )
                flattenedItem._monthsFormatted = Object.freeze(
                    (flattenedItem.months_sum.months as number[]).map(
                        (m: number) => formatDigitsGrouping(Math.round(m * multiplier) / multiplier)
                    )
                )
                // Prevent Vue 2 from installing getters/setters on the numeric data —
                // months_sum is never mutated after flattening.
                Object.freeze(flattenedItem.months_sum.months)
                Object.freeze(flattenedItem.months_sum)
            } else {
                flattenedItem._totalFormatted = ''
                flattenedItem._monthsFormatted = []
            }

            if (flattenedItem.months_sum && isFirstLevel) {
                this.topLevelDatas.push(flattenedItem.months_sum.total)
            }

            result.push(flattenedItem);

            for (const listKey of childLists) {
                if (Array.isArray(item[listKey])) {
                    result.push(...this.flattenData(item[listKey], item, false, multiplier));
                }
            }

            // Flatten project_list items (transferts), attaching parent key for collapse/expand
            if (hasProjectChildren) {
                const selectedLevels: string[] = this.tableData.selected_levels ?? []
                const parentLevelKey = selectedLevels[selectedLevels.length - 1]
                const parentFields = Object.fromEntries(
                    Object.entries(item).filter(([k]) => k.startsWith('parent_'))
                )
                const projectItems = item['project_list'].map((proj: any) => ({
                    ...proj,
                    ...parentFields,
                    ...(parentLevelKey ? { [`parent_${parentLevelKey}`]: item[parentLevelKey] } : {})
                }))
                result.push(...this.flattenData(projectItems, item, false, multiplier))
            }
        }

        return result;
    }

    private determineHierarchy(data: any[]): string[] {
        const hierarchy: string[] = [];

        let currentLevelItems = data;

        while (currentLevelItems && currentLevelItems.length > 0) {
            const firstItem = currentLevelItems[0];

            // Получаем ключ текущего уровня (например, 'gr', 'abp', ...)
            const currentLevelKey = Object.keys(firstItem).find(k =>
                typeof firstItem[k] === 'number' || typeof firstItem[k] === 'string'
            );

            if (!currentLevelKey || hierarchy.includes(currentLevelKey)) break;

            hierarchy.push(currentLevelKey);

            // Ищем следующий list
            const nextListKey = Object.keys(firstItem).find(k => k.endsWith('_list') && Array.isArray(firstItem[k]));

            if (!nextListKey) break;

            currentLevelItems = firstItem[nextListKey];
        }

        return hierarchy;
    }


    private hierarchyLevels: string[] = [];

    private totalRow = {
        total: 0,
        months: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    }

    private getPFRows(): void {
        this.totalRow = {
            total: 0,
            months: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
        }
        this.currentItems = [];

        // Автоопределение первого уровня (gr_list, abp_list и т.п.)
        const topLevelKey = Object.keys(this.tableData).find(k => k.endsWith('_list') && Array.isArray(this.tableData[k]));
        const topData = topLevelKey ? this.tableData[topLevelKey] : [];

        // Определяем текущую иерархию
        this.hierarchyLevels = this.determineHierarchy(topData);

        // Преобразуем в flat список для таблицы
        this.currentItems = this.flattenData(topData, null, true);



        for (const item of topData) {
            this.totalRow.total += item.months_sum.total;
            for (let i = 0; i < item.months_sum.months.length; i++) {
                this.totalRow.months[i] += item.months_sum.months[i]
            }
        }
    }


    private getPPRows() {
        this.totalRow = {
            total: 0,
            months: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
        }
        this.currentItems = [];

        // Автоопределение первого уровня (gr_list, abp_list и т.п.)
        const topLevelKey = Object.keys(this.tableData).find(k => k.endsWith('_list') && Array.isArray(this.tableData[k]));
        const topData = topLevelKey ? this.tableData[topLevelKey] : [];

        // Определяем текущую иерархию
        this.hierarchyLevels = this.determineHierarchy(topData);

        // Преобразуем в flat список для таблицы
        this.currentItems = this.flattenData(topData, null, true);

        for (const item of topData) {
            this.totalRow.total += item.months_sum.total;
            for (let i = 0; i < item.months_sum.months.length; i++) {
                this.totalRow.months[i] += item.months_sum.months[i]
            }
        }
    }

    public totalRows = this.tableData.length

    public currentPage = 1
    public currentItems: any[] = []
    public calcCurrentItems: any[] = []

    private open = false;

    // Populated in created() once tableFields is ready; used in template instead of inline .filter()
    public activeTableFields: TableField[] = []

    // Virtual scroll state
    private virtualStart = 0
    private virtualEnd = WINDOW_SIZE
    private rowHeight = 40   // fallback estimate for unmeasured rows
    private topSpacerHeight = 0
    private bottomSpacerHeight = 0
    private _scrollRaf: number | null = null
    private _scrollPointerTimer: any = null
    private isScrolling = false
    // _-prefixed: Vue 2 does not proxy these → non-reactive.
    // Per-row actual heights + prefix sums for O(1) spacer math.
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    private _rowHeights: number[] = []
    private _prefixSum: number[] = [0]

    // Rows currently visible in the tree (filtered from calcRows)
    private get filteredRows(): any[] {
        return this.calcRows.filter(row => row.visible && !this.isHiddenGuRow(row))
    }

    // The slice of filteredRows that is actually rendered in the DOM
    private get virtualRows(): any[] {
        return this.filteredRows.slice(this.virtualStart, this.virtualEnd)
    }

    private get activeColumnCount(): number {
        return this.activeTableFields.length
    }

    // Recalculate which slice to render based on current scroll position
    private syncVirtualWindow() {
        const container = this.$refs.tableContainer as HTMLElement
        if (!container) return

        // If container.scrollTop > 0 the div itself is scrollable (overflow-y: auto).
        // Otherwise the window (or a parent) is scrolling — derive position from bounding rect.
        let scrolledIntoContainer: number
        if (container.scrollTop > 0) {
            scrolledIntoContainer = container.scrollTop
        } else {
            const containerTop = container.getBoundingClientRect().top
            scrolledIntoContainer = Math.max(0, -containerTop)
        }

        // Use binary search in prefix sum for accurate row lookup with variable heights.
        // Falls back to division if prefix sum not yet built.
        const firstVisibleRow = this.findRowAtScrollPos(scrolledIntoContainer)
        const total = this.filteredRows.length

        // Render SCROLL_BUFFER rows above the viewport and fill the rest downward
        const rawStart = Math.max(0, firstVisibleRow - SCROLL_BUFFER)
        const rawEnd = Math.min(total, rawStart + WINDOW_SIZE)
        // If we're near the bottom, shift start up so window stays WINDOW_SIZE
        const adjustedStart = Math.max(0, rawEnd - WINDOW_SIZE)

        this.virtualStart = adjustedStart
        this.virtualEnd = rawEnd

        // Set spacer heights from prefix sum (exact, not estimated)
        const ps = this._prefixSum
        const end = Math.min(rawEnd, total)
        this.topSpacerHeight = ps.length > adjustedStart ? ps[adjustedStart] : adjustedStart * this.rowHeight
        this.bottomSpacerHeight = ps.length > total
            ? Math.max(0, ps[total] - ps[end])
            : Math.max(0, (total - end) * this.rowHeight)

        // Measure actual row heights after DOM settles — silently updates _prefixSum
        this.$nextTick(() => this.measureRenderedRows())
    }

    private onScroll() {
        // Disable pointer events while scrolling to prevent hover repaints
        if (!this.isScrolling) this.isScrolling = true
        if (this._scrollPointerTimer !== null) clearTimeout(this._scrollPointerTimer)
        this._scrollPointerTimer = setTimeout(() => { this.isScrolling = false }, 150)

        // Use != null (loose) because Vue 2 doesn't proxy _-prefixed properties to the
        // instance, so this._scrollRaf reads as undefined (not null) until first write.
        // undefined !== null is true → would always return early. Loose != handles both.
        // eslint-disable-next-line eqeqeq
        if (this._scrollRaf != null) return
        this._scrollRaf = requestAnimationFrame(() => {
            try {
                this.syncVirtualWindow()
            } finally {
                this._scrollRaf = null
            }
        })
    }

    // Resync virtual window whenever visible rows change (expand/collapse or data load)
    @Watch('filteredRows')
    private onFilteredRowsChanged() {
        this.$nextTick(() => {
            // Re-measure rowHeight estimate from first rendered row
            const container = this.$refs.tableContainer as HTMLElement
            if (container) {
                const firstRow = container.querySelector('tbody tr') as HTMLElement | null
                if (firstRow && firstRow.offsetHeight > 0) {
                    this.rowHeight = firstRow.offsetHeight
                }
            }
            // Reinitialize per-row heights for the new filteredRows set
            this.initRowHeights(this.filteredRows.length)
            this.syncVirtualWindow()
        })
    }

    @Watch('roundUp')
    private onRoundUpChanged() {
        const multiplier = Math.pow(10, Number(this.roundUp))
        for (const row of this.calcRows) {
            if (!row.months_sum) continue
            row._totalFormatted = formatDigitsGrouping(Math.round(row.months_sum.total * multiplier) / multiplier)
            row._monthsFormatted = Object.freeze(
                (row.months_sum.months as number[]).map(
                    (m: number) => formatDigitsGrouping(Math.round(m * multiplier) / multiplier)
                )
            )
        }
    }

    private thStyle(header: TableField): Record<string, string> {
        if (header.key === 'action') return { width: '50px' }
        if (this.availableLevels.includes(header.key)) return { width: '150px' }
        const w = `${Math.max(80, this.totalLength * 13)}px`
        return { width: w }
    }

    public allColumnsNameMap: Record<string, string> = {
        'action': '',
        'region': 'Регион',
        'kat': 'КАТ',
        'cls': 'КЛ',
        'pcl': 'ПКЛ',
        'gr': 'ФГ',
        'pgr': 'ФПГ',
        'abp': 'АБП',
        'prg': 'БП',
        'ppr': 'БПП',
        'spf': 'СПФ',
        'gu': 'ГУ',
        'transfert': 'Трансферт',
        'total_amount': 'Всего',
        'month1': 'январь',
        'month2': 'февраль',
        'month3': 'март',
        'month4': 'апрель',
        'month5': 'май',
        'month6': 'июнь',
        'month7': 'июль',
        'month8': 'август',
        'month9': 'сентябрь',
        'month10': 'октябрь',
        'month11': 'ноябрь',
        'month12': 'декабрь',
    }


    public columnsNameMap: Record<string, string> = {
        'action': '',
        'total_amount': 'Всего',
        'month1': 'январь',
        'month2': 'февраль',
        'month3': 'март',
        'month4': 'апрель',
        'month5': 'май',
        'month6': 'июнь',
        'month7': 'июль',
        'month8': 'август',
        'month9': 'сентябрь',
        'month10': 'октябрь',
        'month11': 'ноябрь',
        'month12': 'декабрь',
    }



    private availableLevels = ['region', 'kat', 'cls', 'pcl', 'gr', 'pgr', 'abp', 'prg', 'ppr', 'spf', 'gu', 'transfert'];

    public tableFields: TableField[] = (() => {
        const selectedCols = this.tableData.selected_levels || [];
        const showTransfertCol = !!this.tableData.show_transferts;

        // gu is only visible when both abp and prg are selected
        const guVisible = selectedCols.includes('gu') && selectedCols.includes('abp') && selectedCols.includes('prg');

        // Preserve user's column order; only enforce gu position between abp and prg
        let orderedCols = [...selectedCols];
        if (orderedCols.includes('gu')) {
            orderedCols = orderedCols.filter((c: string) => c !== 'gu');
            const abpIdx = orderedCols.indexOf('abp');
            const prgIdx = orderedCols.indexOf('prg');
            if (abpIdx !== -1) {
                orderedCols.splice(abpIdx + 1, 0, 'gu');
            } else if (prgIdx !== -1) {
                orderedCols.splice(prgIdx, 0, 'gu');
            } else {
                orderedCols.push('gu');
            }
        }

        const orderedKeys: string[] = [
            'action',
            ...orderedCols,
            ...(showTransfertCol ? ['transfert'] : []),
            'total_amount',
            ...Array.from({ length: 12 }, (_, i) => `month${i + 1}`),
        ];
        return orderedKeys.map((key) => {
            const label = this.allColumnsNameMap[key] ?? key;
            const isAvailableLevel = this.availableLevels.includes(key);
            const isActive = isAvailableLevel
                ? (key === 'transfert' ? showTransfertCol : key === 'gu' ? guVisible : selectedCols.includes(key))
                : true;
            const monthMatch = key.match(/^month(\d+)$/)

            return {
                key: key as Column,
                label,
                active: isActive,
                ...(monthMatch ? { monthIndex: parseInt(monthMatch[1]) - 1 } : {}),
            };
        });
    })();



    public formatDigitsGrouping = formatDigitsGrouping

    private get guVisible(): boolean {
        const selectedCols = this.tableData.selected_levels || [];
        return selectedCols.includes('gu') && selectedCols.includes('abp') && selectedCols.includes('prg');
    }

    private isHiddenGuRow(row: any): boolean {
        if (this.guVisible) return false;
        const selectedLevels: string[] = this.tableData.selected_levels || [];
        if (!selectedLevels.includes('gu')) return false;
        const rowLevel = selectedLevels.find((level: string) => row[level] !== undefined);
        return rowLevel === 'gu';
    }

    private openChilds(item: any) {
        item.open = !item.open
        const selectedLevels = this.tableData.selected_levels;
        const key = selectedLevels.find((level: any) => item[level] !== undefined);
        if (item.open) {
            const keyIndx = selectedLevels.indexOf(key);
            const nextKey = selectedLevels[keyIndx + 1]
            let childList = this.calcRows;

            if (nextKey === undefined && this.tableData.show_transferts) {
                // Last KBK level (e.g. spf) — show its project_list rows
                for (let i = 0; i < keyIndx; i++) {
                    const childKey = selectedLevels[i]
                    childList = childList.filter(curItem => curItem['parent_' + childKey] === item['parent_' + childKey])
                }
                childList = childList.filter(curItem =>
                    curItem['expense_code'] !== undefined &&
                    curItem['parent_' + key] === item[key]
                )
            } else {
                for (let i = 0; i <= keyIndx; i++) {
                    const childKey = selectedLevels[i]
                    if (i === keyIndx) {
                        childList = childList.filter(curItem => curItem[nextKey] && curItem['parent_' + childKey] === item[childKey])
                    } else {
                        childList = childList.filter(curItem => curItem[nextKey] && curItem['parent_' + childKey] === item['parent_' + childKey])
                    }
                }
            }

            for (const child of childList) {
                child.visible = item.open;
            }
        }
        if (!item.open) {
            const keyIndx = selectedLevels.indexOf(key);
            for (let i = keyIndx; i < selectedLevels.length; i++) {
                const childKey = selectedLevels[i];
                const childLevel = this.calcRows.filter(curItem => curItem['parent_' + childKey] && curItem['parent_' + childKey] === item[childKey])
                for (const child of childLevel) {
                    child.visible = item.open;
                    child.open = item.open;
                }
            }
            // Also hide project rows that belong to this item
            if (this.tableData.show_transferts) {
                const projectRows = this.calcRows.filter(curItem =>
                    curItem['expense_code'] !== undefined &&
                    curItem['parent_' + key] === item[key]
                )
                for (const child of projectRows) {
                    child.visible = false;
                }
            }
        }
    }

    private openAll() {
        this.open = !this.open;
        const firstItem = this.tableData.selected_levels[0];
        for (const r of this.calcRows) {
            if (!r[firstItem]) {
                r.open = this.open;
                r.visible = this.open;
            } else {
                r.open = this.open;
            }
        }
    }

    private calcRows: any[] = []

    private calcItems() {
        // cloneDeep removed: the commented-out month-adjustment algorithm was the only
        // mutation, and without it the clone bought nothing while costing O(n²) time
        // (each row still carries its _list subtrees from ...item in flattenData).
        this.calcRows = this.currentItems
    }

//    @Watch('calcOnCurPeriod')
//     onCalcOnCurPeriodChanged(newVal: any, oldVal: any) {
//         this.calcItems();
//     }
}
